import {
  useMutation,
  UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';

import APIError from 'types/api_error';
import { Deployment } from 'types/deployment';

import { useAPI } from 'hooks/api/useAPI';
import {
  updateQueriesData,
  UpdateQueriesDataContext,
} from 'services/update_queries_data';

export function useDeleteDeployments({
  onMutate,
  onError,
  onSettled,
  ...options
}: UseMutationOptions<
  void,
  APIError,
  { deployment_ids: Array<string> },
  UpdateQueriesDataContext<Deployment> | void
> = {}) {
  const queryClient = useQueryClient();
  const { applicationID, client } = useAPI();

  return useMutation(
    async ({ deployment_ids }) => {
      await Promise.all(
        deployment_ids.map((id) =>
          client.delete(`/apps/${applicationID}/deployments/${id}`)
        )
      );
    },
    {
      ...options,
      async onMutate({ deployment_ids }) {
        await queryClient.cancelQueries(['deployments']);
        await queryClient.cancelQueries(['deployment']);

        onMutate?.({ deployment_ids });

        return updateQueriesData<Deployment>({
          queryClient,
          listQueryKey: ['deployments'],
          singleQueryKey: ['deployment'],
          ids: deployment_ids,
          updateData: null,
        });
      },
      onError(error, params, context) {
        onError?.(error, params, context);

        context?.previousLists?.forEach(([queryKey, data]) =>
          queryClient.setQueryData(queryKey, data)
        );
        context?.previousSingles?.forEach(([queryKey, data]) =>
          queryClient.setQueryData(queryKey, data)
        );
      },
      async onSettled(...args) {
        onSettled?.(...args);

        queryClient.invalidateQueries(['deployments']);
        queryClient.invalidateQueries(['deployment']);
      },
    }
  );
}
